import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getGrowthReports_getGrowthReports_growthReports_reports as StudentGrowthReportData } from '../../../../schema/graphql-types';
import { AppState } from '../../../app/core/core.state';
import {
  AssessmentReportingType,
  DomainKey,
  Domains,
} from '../../multi-stage-growth-report/models/multi-stage-growth-report.model';
import { TranslatedReportingTypeValue } from '../../pages/overall-scale-score-summary/models/overall-scale-score-summary.model';
import { DEFAULT_METRIC_CHART_SETTINGS, DEFAULT_SCALE_SCORE_CHART_SETTINGS } from '../common.constant';
import { GrowthChartCheckboxSettings, GrowthChartSettings, InputParams, Locale } from '../models/common.model';
import { CommandQueryState } from '../models/common.store.model';
import { AssessmentReportingTypes } from '../models/constants';
import {
  ActionResetSharedStore,
  ActionSetCommandQuery,
  ActionSetInputParams,
  ActionSetIsLoading,
  ActionSetIsMultiStageReportLoading,
  ActionSetLocale,
  ActionSetShowSideNav,
} from '../store/shared.actions';
import {
  selectCommandQuery,
  selectInputParams,
  selectIsLoading,
  selectIsMultiStageReportLoading,
  selectLocale,
  selectShowSideNav,
} from '../store/shared.selectors';
@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private scaleScoreChartSettings: GrowthChartSettings = { ...DEFAULT_SCALE_SCORE_CHART_SETTINGS };
  private metricChartSettings: GrowthChartCheckboxSettings = { ...DEFAULT_METRIC_CHART_SETTINGS };

  constructor(private readonly store$: Store<AppState>, private readonly translateService: TranslateService) {}

  isLoading$ = this.store$.pipe(select(selectIsLoading));
  isMultiStageReportLoading$ = this.store$.pipe(select(selectIsMultiStageReportLoading));
  commandQuery$ = this.store$.pipe(select(selectCommandQuery));
  locale$ = this.store$.pipe(select(selectLocale));
  selectInputParams$ = this.store$.pipe(select(selectInputParams));
  showSideNav$ = this.store$.pipe(select(selectShowSideNav));

  setIsLoading(isLoading: boolean): void {
    this.store$.dispatch(ActionSetIsLoading({ isLoading }));
  }

  setIsMultiStageReportLoading(isMultiStageReportLoading: boolean): void {
    this.store$.dispatch(ActionSetIsMultiStageReportLoading({ isMultiStageReportLoading }));
  }

  setLocale(locale: Locale): void {
    this.translateService.use(locale);
    this.store$.dispatch(ActionSetLocale({ locale }));
  }

  setCommandQuery(commandQuery: CommandQueryState): void {
    this.store$.dispatch(ActionSetCommandQuery({ commandQuery }));
  }

  setInputParams(inputParams: InputParams): void {
    this.store$.dispatch(ActionSetInputParams({ inputParams }));
  }

  setShowSidenav(showSideNav: boolean): void {
    this.store$.dispatch(ActionSetShowSideNav({ showSideNav }));
  }

  resetSharedStore(): void {
    this.store$.dispatch(ActionResetSharedStore());
  }

  getAssessmentTypeLabel(
    assessmentReportingType: AssessmentReportingType,
    translatedReportingTypeValues: TranslatedReportingTypeValue,
  ): string {
    switch (assessmentReportingType) {
      case AssessmentReportingType.EOY:
        return translatedReportingTypeValues.EOY;
      case AssessmentReportingType.MOY:
        return translatedReportingTypeValues.MOY;
      case AssessmentReportingType.BOY:
        return translatedReportingTypeValues.BOY;
    }
  }

  calculateClassAverageGrowth(
    classAverageGrowthScore: Map<AssessmentReportingType, Map<string, number>>,
    domainName: string,
  ): number {
    if (domainName === Domains.OV) {
      domainName = DomainKey.OV;
    }
    let classAverageScore = 0;
    const classAverageGrowthScoreMapLength = classAverageGrowthScore.size;
    const classAverageGrowthScoreMapValues = Array.from(classAverageGrowthScore.values());
    if (classAverageGrowthScoreMapLength > 1) {
      classAverageScore =
        classAverageGrowthScoreMapValues[classAverageGrowthScoreMapLength - 1].get(domainName) -
        classAverageGrowthScoreMapValues[0].get(domainName);
    } else {
      classAverageScore = null;
    }
    return classAverageScore;
  }

  calculateClassAverageGrowthScore(
    classAverageGrowthScoreMap: Map<AssessmentReportingType, Map<string, number>>,
    selectedDomain: string,
  ): number {
    // sort the map based on the order of assessmentReportingType - EOY > MOY > BOY
    const classAverageGrowthScoreMapSortedMap = this.sortStudentGrowthReportWithReportingType(
      classAverageGrowthScoreMap,
    ) as Map<AssessmentReportingType, Map<string, number>>;

    return this.calculateClassAverageGrowth(classAverageGrowthScoreMapSortedMap, selectedDomain);
  }

  sortStudentGrowthReportWithReportingType(
    studentGrowthReport: Map<AssessmentReportingType, StudentGrowthReportData | Map<string, number>>,
  ): Map<AssessmentReportingType, StudentGrowthReportData | Map<string, number>> {
    const assessmentReportingTypes = [
      AssessmentReportingType.BOY,
      AssessmentReportingType.MOY,
      AssessmentReportingType.EOY,
    ];
    const sortedStudentReports = new Map<AssessmentReportingType, StudentGrowthReportData | Map<string, number>>();
    // Create a new Map with the sorted entries
    assessmentReportingTypes.forEach((assessmentReportingType) => {
      const value = studentGrowthReport.get(assessmentReportingType);
      if (value) {
        sortedStudentReports.set(assessmentReportingType, value);
      }
    });
    return sortedStudentReports;
  }

  calculateDistrictAverageGrowth(districtDomainScores: number[]): number {
    let districtAverageGrowthScore = 0;
    const filteredDistrictDomainScores = districtDomainScores.filter((score) => score !== null);
    if (filteredDistrictDomainScores.length > 1) {
      districtAverageGrowthScore =
        filteredDistrictDomainScores[filteredDistrictDomainScores.length - 1] - filteredDistrictDomainScores[0];
    } else {
      districtAverageGrowthScore = 0;
    }
    return districtAverageGrowthScore;
  }

  sortStudentGrowthReportWithReportingTypeForClassAverageMetric(
    //sorting class average metric score map and removeing null reportings too.
    studentGrowthReport: Map<AssessmentReportingType, string>,
  ): Map<AssessmentReportingType, string> {
    const sortedStudentReports = new Map<AssessmentReportingType, string>();
    // Create a new Map with the sorted entries
    AssessmentReportingTypes.forEach((assessmentReportingType) => {
      const value = studentGrowthReport.get(assessmentReportingType);
      if (value) {
        sortedStudentReports.set(assessmentReportingType, value);
      }
    });
    return sortedStudentReports;
  }

  getScaleScoreChartSettings(): GrowthChartSettings {
    return { ...this.scaleScoreChartSettings };
  }

  setScaleScoreChartSettings(settings: Partial<GrowthChartSettings>): void {
    this.scaleScoreChartSettings = { ...this.scaleScoreChartSettings, ...settings };
  }

  resetScaleScoreChartSettings(): void {
    this.scaleScoreChartSettings = { ...DEFAULT_SCALE_SCORE_CHART_SETTINGS };
  }

  getMetricChartSettings(): GrowthChartCheckboxSettings {
    return { ...this.metricChartSettings };
  }

  setMetricChartSettings(settings: Partial<GrowthChartCheckboxSettings>): void {
    this.metricChartSettings = { ...this.metricChartSettings, ...settings };
  }

  resetMetricChartSettings(): void {
    this.metricChartSettings = { ...DEFAULT_METRIC_CHART_SETTINGS };
  }
}
